import { create } from "zustand";
import apiManager from "utils/api/apiManager";
import {
  authLocalStorage,
  removeAuthFromLocalStorage,
} from "utils/helpers/auth";

type LoginResponse = {
  accessToken: string;
  refreshToken: string;
};

type User = {
  withContentCreatorRole: boolean;
  email: string;
  withCodeExporterRole: boolean;
  accessKeys?: string[];
};

type Malls = {
  id: string;
  title: string;
  hasHomePage: boolean;
  floors: string[];
}[];

type InitialState = {
  user: User | null;
  loading: boolean;
  getUserLoading: boolean;
  accessibleMalls: Malls | null;
  getAccessibleMallsLoading: boolean;

  getAccessibleMalls: () => Promise<void>;
  stopUserLoading: () => void;
  reset: () => void;
  getUser: () => Promise<void>;
  login: (
    emial: string,
    password: string,
    keepMe?: boolean,
  ) => Promise<boolean>;
  forgotPassword: (email: string) => Promise<boolean>;
  resetPassword: (
    email: string,
    newPassword: string,
    resetToken: string,
  ) => Promise<boolean>;
  setPassword: (
    email: string,
    newPassword: string,
    resetToken: string,
  ) => Promise<boolean>;
  changePassword: (
    oldPassword: string,
    newPassword: string,
  ) => Promise<boolean>;

  logout: () => void;
};

const initialValues = {
  loading: false,
  getUserLoading: true,
  user: null,
  accessibleMalls: null,
  getAccessibleMallsLoading: false,
};

export const useAuth = create<InitialState>((set, get) => ({
  ...initialValues,
  stopUserLoading: () => set({ getUserLoading: false }),
  reset: () => {
    set({ ...initialValues, getUserLoading: false });
  },

  getAccessibleMalls: async () => {
    set({ getAccessibleMallsLoading: true });
    try {
      const res = await apiManager.getData<Malls>(
        "Identity/getAccessibleMalls",
      );

      set({ accessibleMalls: res.response });
    } catch (err) {
      console.error("Error in data fetch:", err);
    } finally {
      set({ getAccessibleMallsLoading: false });
    }
  },

  getUser: async () => {
    set({ getUserLoading: true });
    try {
      const res = await apiManager.getData<User>("Identity/getUser");

      set({ user: res.response });
    } catch (err) {
      console.error("Error in data fetch:", err);
    } finally {
      set({ getUserLoading: false });
    }
  },
  login: async (email: string, password: string, keepMe: boolean = false) => {
    set({ loading: true });
    try {
      const res = await apiManager.postData<LoginResponse>("Identity/login", {
        email,
        password,
        keepMe,
      });

      if (res.isError) return res.isError;

      authLocalStorage({
        token: res.response?.accessToken ?? "",
        refreshToken: res.response?.refreshToken ?? "",
        keepMe: keepMe,
      });

      await get().getUser();

      return !res.isError;
    } catch (err) {
      console.error("Error in data fetch:", err);
      return false;
    } finally {
      set({ loading: false });
    }
  },
  forgotPassword: async (email: string) => {
    set({ loading: true });
    try {
      const res = await apiManager.postData("Identity/ForgotPassword", {
        email,
      });

      return !res.isError;
    } catch (err) {
      console.error("Error in data fetch:", err);
      return false;
    } finally {
      set({ loading: false });
    }
  },
  resetPassword: async (
    email: string,
    newPassword: string,
    resetToken: string,
  ) => {
    set({ loading: true });
    try {
      const res = await apiManager.postData("Identity/resetPassword", {
        email,
        newPassword,
        resetToken,
      });

      return !res.isError;
    } catch (err) {
      console.error("Error in data fetch:", err);
      return false;
    } finally {
      set({ loading: false });
    }
  },
  setPassword: async (
    email: string,
    password: string,
    confirmationToken: string,
  ) => {
    set({ loading: true });
    try {
      const res = await apiManager.postData("Identity/setPassword", {
        email,
        password,
        confirmationToken,
      });

      return !res.isError;
    } catch (err) {
      console.error("Error in data fetch:", err);
      return false;
    } finally {
      set({ loading: false });
    }
  },
  changePassword: async (oldPassword: string, newPassword: string) => {
    set({ loading: true });
    try {
      const res = await apiManager.postData("Identity/changePassword", {
        oldPassword,
        newPassword,
      });

      return !res.isError;
    } catch (err) {
      console.error("Error in data fetch:", err);
      return false;
    } finally {
      set({ loading: false });
    }
  },
  // non api call methods
  logout: () => {
    removeAuthFromLocalStorage();
    get().reset();
    window.location.reload();
  },
}));
